<template>
  <div class="feature-organizations">
    <a-alert
      v-if="feature.suppressed"
      type="warning"
      show-icon
      class="mb-3"
      message="This feature is disabled for everyone. The organizations below get it back when you re-enable it."
    />

    <a-row :gutter="[8, 8]" class="mb-3">
      <a-col :xs="24" :md="10" :lg="8">
        <a-select
          v-model:value="organizationToEnable"
          :options="availableOrganizationOptions"
          :filter-option="filterOrganizationOption"
          :disabled="feature.suppressed"
          placeholder="Enable for organization..."
          show-search
          allow-clear
          class="w-100"
        />
      </a-col>
      <a-col>
        <a-button
          type="primary"
          :disabled="!organizationToEnable || feature.suppressed"
          :loading="!!busy[`sub:${feature.id}:${organizationToEnable}`]"
          @click="handleEnable"
        >
          Enable
        </a-button>
      </a-col>
      <a-col v-if="subscriptions.length > pageSize" :xs="24" :md="8" :lg="6">
        <a-input
          v-model:value="organizationSearch"
          placeholder="Search organizations"
          allow-clear
        />
      </a-col>
    </a-row>

    <a-table
      :columns="columns"
      :data-source="visibleSubscriptions"
      :pagination="{ pageSize, size: 'small', hideOnSinglePage: true }"
      row-key="id"
      size="small"
    >
      <template #bodyCell="{ record, column }">
        <template v-if="column.key === 'organization'">
          {{ organizationLabel(record.Organization) }}
          <span
            v-if="
              record.Organization.org_name_to_display &&
              record.Organization.org_name_to_display !==
                record.Organization.Org_name
            "
            class="text-secondary"
          >
            ({{ record.Organization.Org_name }})
          </span>
        </template>
        <template v-if="column.key === 'actions'">
          <a-popconfirm
            :title="`Remove ${feature.name} from ${organizationLabel(
              record.Organization
            )}? Other organizations are not affected.`"
            ok-text="Remove access"
            cancel-text="Cancel"
            @confirm="$emit('remove', record)"
          >
            <a-button
              type="link"
              size="small"
              :loading="
                !!busy[`sub:${feature.id}:${record.Organization.Org_name}`]
              "
            >
              Remove access
            </a-button>
          </a-popconfirm>
        </template>
      </template>
      <template #emptyText>
        <a-empty
          :description="
            subscriptions.length
              ? 'No organizations match your search.'
              : 'No organization has this feature yet.'
          "
        />
      </template>
    </a-table>
  </div>
</template>

<script>
import { organizationLabel } from './featureControlMixin';

export default {
  name: 'FeatureOrganizations',
  props: {
    feature: { type: Object, required: true },
    subscriptions: { type: Array, required: true },
    organizationOptions: { type: Array, required: true },
    busy: { type: Object, required: true },
  },
  emits: ['enable', 'remove'],
  data() {
    return {
      organizationToEnable: undefined,
      organizationSearch: '',
      pageSize: 10,
      columns: [
        { title: 'Organization', key: 'organization' },
        { title: '', key: 'actions', width: 160, align: 'right' },
      ],
    };
  },
  computed: {
    // Organizations that do not have this feature yet.
    availableOrganizationOptions() {
      const subscribed = new Set(
        this.subscriptions.map((item) => item.Organization?.Org_name)
      );
      return this.organizationOptions.filter(
        (option) => !subscribed.has(option.value)
      );
    },
    visibleSubscriptions() {
      const sorted = [...this.subscriptions].sort((a, b) =>
        organizationLabel(a.Organization).localeCompare(
          organizationLabel(b.Organization)
        )
      );
      const search = this.organizationSearch.trim().toLowerCase();
      if (!search) return sorted;
      return sorted.filter((item) =>
        `${organizationLabel(item.Organization)} ${item.Organization?.Org_name}`
          .toLowerCase()
          .includes(search)
      );
    },
  },
  methods: {
    organizationLabel,
    filterOrganizationOption(input, option) {
      const search = input.toLowerCase();
      return [option.label, option.value].some((text) =>
        String(text ?? '')
          .toLowerCase()
          .includes(search)
      );
    },
    handleEnable() {
      const option = this.organizationOptions.find(
        (item) => item.value === this.organizationToEnable
      );
      this.$emit('enable', {
        organization: this.organizationToEnable,
        organizationName: option?.label || this.organizationToEnable,
      });
      this.organizationToEnable = undefined;
    },
  },
};
</script>

<style scoped>
.feature-organizations {
  padding: 8px 8px 8px 32px;
}
</style>
